import { OlympicsEvent } from '../validation/scheduleDataSchemas';

/**
 * Parses date from URL parameter (?date=2026-02-10)
 */
function getDateFromURL(): Date | null {
  const params = new URLSearchParams(window.location.search);
  const dateParam = params.get('date');

  if (!dateParam) {
    return null;
  }

  try {
    const [year, month, day] = dateParam.split('-').map(Number);
    if (!year || !month || !day) {
      return null;
    }
    const date = new Date(year, month - 1, day); // month is 0-indexed in JS
    return date;
  } catch (error) {
    return null;
  }
}

/**
 * Extracts all unique dates from events and returns them sorted
 * Returns dates as Date objects in chronological order
 */
export function extractAllDates(events: OlympicsEvent[]): Date[] {
  const dateSet = new Set<number>();

  events.forEach((event) => {
    const [datePart] = event.date.split(' ');
    const [year, month, day] = datePart.split('-').map(Number);
    const eventDate = new Date(year, month - 1, day);
    dateSet.add(eventDate.getTime());
  });

  return Array.from(dateSet)
    .sort((a, b) => a - b)
    .map((timestamp) => new Date(timestamp));
}

/**
 * Groups events by date string (YYYY-MM-DD format)
 */
export function groupEventsByDate(events: OlympicsEvent[]): Map<string, OlympicsEvent[]> {
  const grouped = new Map<string, OlympicsEvent[]>();

  events.forEach((event) => {
    const [datePart] = event.date.split(' ');
    if (!grouped.has(datePart)) {
      grouped.set(datePart, []);
    }
    grouped.get(datePart)!.push(event);
  });

  // Sort events within each day by time
  grouped.forEach((dayEvents) => {
    dayEvents.sort((a, b) => a.date.localeCompare(b.date));
  });

  return grouped;
}

/**
 * Categorizes events by date (yesterday, today, tomorrow)
 * Uses date from URL parameter (?date=2026-02-10) if provided,
 * otherwise uses 2026-02-14 (busiest day with 85 events)
 */
export function categorizeEventsByDate(events: OlympicsEvent[]): Record<string, OlympicsEvent[]> {
  if (events.length === 0) {
    return { yesterday: [], today: [], tomorrow: [] };
  }

  // Check if date is specified in URL, otherwise use default date
  const urlDate = getDateFromURL();
  const supposedToday = urlDate || new Date(2026, 1, 14); // Feb 14, 2026 (month is 0-indexed)
  const supposedYesterday = new Date(supposedToday);
  supposedYesterday.setDate(supposedYesterday.getDate() - 1);
  const supposedTomorrow = new Date(supposedToday);
  supposedTomorrow.setDate(supposedTomorrow.getDate() + 1);

  const categorized: Record<string, OlympicsEvent[]> = {
    yesterday: [],
    today: [],
    tomorrow: [],
  };

  events.forEach((event) => {
    // Date format from merged-data: "2022-02-05 12:45:00"
    const [datePart] = event.date.split(' ');
    const [year, month, day] = datePart.split('-').map(Number);
    const eventDay = new Date(year, month - 1, day); // month is 0-indexed in JS

    if (eventDay.getTime() === supposedYesterday.getTime()) {
      categorized.yesterday.push(event);
    } else if (eventDay.getTime() === supposedToday.getTime()) {
      categorized.today.push(event);
    } else if (eventDay.getTime() === supposedTomorrow.getTime()) {
      categorized.tomorrow.push(event);
    }
  });

  // Sort events by time within each day
  Object.keys(categorized).forEach((key) => {
    categorized[key].sort((a, b) => {
      return a.date.localeCompare(b.date);
    });
  });

  return categorized;
}
